import i18n from "i18next";
import HttpBackend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";

/**
 * i18n Service - Internationalization Initialization
 *
 * This module initializes and configures i18next for the application.
 * Designed with PWA best practices:
 * - Translations cached by service worker for offline access
 * - Lazy loading of non-critical namespaces
 * - Language preference persisted in localStorage
 * - Browser language detection with fallback
 *
 * USAGE:
 * 1. Import and call initI18n() during app initialization
 * 2. Use useTranslate() hook in components
 *
 * @example
 * // In App initialization
 * await initI18n();
 *
 * // In components
 * const { t } = useTranslate();
 * return <h1>{t('welcome')}</h1>;
 */
import {
  BACKEND_CONFIG,
  DEFAULT_LANGUAGE,
  DEFAULT_NAMESPACE,
  DEFAULT_NAMESPACES,
  I18N_DEBUG,
  INTERPOLATION_CONFIG,
  REACT_CONFIG,
} from "./i18n-config";
import type { I18nInitOptions, LanguageCode } from "./types";
import { LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES } from "./types";

/**
 * Detect the best initial language based on:
 * 1. Stored preference in localStorage
 * 2. Browser language
 * 3. Default fallback (English)
 *
 * @returns The language code to use on initialization
 */
const detectInitialLanguage = (): LanguageCode => {
  // Check for stored preference
  const storedLanguage = localStorage.getItem(
    LANGUAGE_STORAGE_KEY
  ) as LanguageCode | null;

  if (storedLanguage && storedLanguage in SUPPORTED_LANGUAGES) {
    return storedLanguage;
  }

  // Detect browser language
  const browserLanguage = navigator.language.split("-")[0] as LanguageCode;

  if (browserLanguage in SUPPORTED_LANGUAGES) {
    return browserLanguage;
  }

  // Fallback to default
  return DEFAULT_LANGUAGE;
};

/**
 * Persist language preference to localStorage
 * Called automatically when language changes
 *
 * @param language - The language code to persist
 */
const persistLanguagePreference = (language: LanguageCode): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.warn("[i18n] Failed to persist language preference:", error);
  }
};

/**
 * Initialize the i18n system
 *
 * This function should be called once during application startup,
 * before rendering any components that use translations.
 *
 * @param options - Optional initialization options
 * @returns Promise that resolves when i18n is ready
 *
 * @example
 * // Basic initialization
 * await initI18n();
 *
 * @example
 * // With custom options
 * await initI18n({
 *   initialLanguage: 'fr',
 *   defaultNamespaces: ['common', 'session'],
 * });
 */
export const initI18n = async (options?: I18nInitOptions): Promise<void> => {
  if (i18n.isInitialized) {
    return;
  }

  const initialLanguage = options?.initialLanguage ?? detectInitialLanguage();
  const namespaces = options?.defaultNamespaces ?? DEFAULT_NAMESPACES;

  // Initialize i18next with HTTP backend and React bindings
  await i18n
    .use(HttpBackend)
    .use(initReactI18next)
    .init({
      // Language configuration
      lng: initialLanguage,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: Object.keys(SUPPORTED_LANGUAGES),

      // Namespace configuration
      ns: namespaces,
      defaultNS: DEFAULT_NAMESPACE,

      // Backend configuration (loads from /locales)
      backend: BACKEND_CONFIG,

      // Interpolation settings
      interpolation: INTERPOLATION_CONFIG,

      // React-specific settings
      react: REACT_CONFIG,

      // Debug mode (development only)
      debug: I18N_DEBUG,

      // Return key if translation not found (useful for development)
      returnEmptyString: false,

      // Key separator for nested keys (e.g., 'settings.display.theme')
      keySeparator: ".",

      // Namespace separator for cross-namespace references (e.g., 'common:cancel')
      nsSeparator: ":",

      // PWA optimization: preload critical namespaces
      preload: [DEFAULT_LANGUAGE],
    });

  // Persist initial language preference
  persistLanguagePreference(initialLanguage);

  // Listen for language changes and persist preference
  i18n.on("languageChanged", (newLanguage: string) => {
    persistLanguagePreference(newLanguage as LanguageCode);

    // Update document direction for RTL languages
    const language = SUPPORTED_LANGUAGES[newLanguage as LanguageCode];
    if (language?.isRTL) {
      document.documentElement.setAttribute("dir", "rtl");
    } else {
      document.documentElement.setAttribute("dir", "ltr");
    }

    // Update document lang attribute for accessibility
    document.documentElement.setAttribute("lang", newLanguage);
  });

  // Set initial document attributes
  document.documentElement.setAttribute("lang", initialLanguage);
  const initialLangOption = SUPPORTED_LANGUAGES[initialLanguage];
  document.documentElement.setAttribute(
    "dir",
    initialLangOption?.isRTL ? "rtl" : "ltr"
  );
};

/**
 * Change the current language
 *
 * This function changes the active language and persists the preference.
 * All components using useTranslation will automatically re-render.
 *
 * @param language - The language code to switch to
 * @returns Promise that resolves when language is loaded
 *
 * @example
 * await changeLanguage('fr');
 */
export const changeLanguage = async (language: LanguageCode): Promise<void> => {
  if (!(language in SUPPORTED_LANGUAGES)) {
    console.warn(`[i18n] Unsupported language: ${language}`);
    return;
  }

  await i18n.changeLanguage(language);
};

/**
 * Get the current language
 *
 * @returns Current language code
 */
export const getCurrentLanguage = (): LanguageCode => {
  return i18n.language as LanguageCode;
};

/**
 * Check if i18n is initialized and ready
 *
 * @returns True if i18n is ready to use
 */
export const isI18nReady = (): boolean => {
  return i18n.isInitialized;
};

/**
 * Load additional namespaces on demand
 *
 * Use this to lazy-load namespaces that aren't needed on initial render.
 * The service worker will cache these for offline access.
 *
 * @param namespaces - Namespace(s) to load
 * @returns Promise that resolves when namespaces are loaded
 *
 * @example
 * // Load single namespace
 * await loadNamespaces('session');
 *
 * @example
 * // Load multiple namespaces
 * await loadNamespaces(['session', 'settings']);
 */
export const loadNamespaces = async (
  namespaces: string | string[]
): Promise<void> => {
  await i18n.loadNamespaces(namespaces);
};

/**
 * Export the i18n instance for advanced usage
 * Prefer using the helper functions above for most cases
 */
export { i18n };

// =============================================================================
// CROWDIN INTEGRATION (COMMENTED - Enable when Crowdin is configured)
// =============================================================================

/**
 * To enable Crowdin OTA integration:
 *
 * 1. Install the Crowdin OTA client:
 *    npm install @crowdin/ota-client i18next-chained-backend
 *
 * 2. Update imports:
 *    import ChainedBackend from 'i18next-chained-backend';
 *    import { CrowdinFallbackBackend } from './crowdin-backend';
 *
 * 3. Replace the .use(HttpBackend) with:
 *    .use(ChainedBackend)
 *
 * 4. Update backend config:
 *    backend: {
 *      backends: [CrowdinFallbackBackend, HttpBackend],
 *      backendOptions: [
 *        {
 *          distributionHash: CROWDIN_CONFIG.distributionHash,
 *          distributionName: CROWDIN_CONFIG.distributionName,
 *          referenceLng: CROWDIN_CONFIG.referenceLng,
 *        },
 *        BACKEND_CONFIG,
 *      ],
 *    },
 *
 * 5. Uncomment CROWDIN_CONFIG in i18n-config.ts
 *
 * 6. Uncomment CrowdinFallbackBackend in crowdin-backend.ts
 */
